import { ampLogEvent } from "analytics/amplitude/amplitudeAnalytics";
import { FACEBOOK_PIXEL_EVENT_NAME, fbPixelTrack } from "analytics/facebook/FacebookPixelEvent";
import KlaviyoTrackingService from "analytics/klaviyo/klaviyoEvents";
import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import { IEProduct } from "api/interfaces/product-api.type";
import { IVehicle } from "interfaces/session/IVehicle";
import { IOrderedItemView } from "interfaces/session/ShoppingCart";
import tryCatchProxy from "modules/lib/tryCatchProxy";
import { IShoppingCartStateReducer } from "reducer/shopping-cart.reducer";
import { getStore } from "store";
import { isTier1Page, isTier2Page, isTier3Page } from "util/helper/helper";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import { isEmpty } from "../../util/lodash/isEmpty";

export interface ICategory {
  tier0: string;
  tier1?: string;
  tier2?: string;
  tier3?: string;
}

export interface HearAboutUs {
  email: string;
  answer: string;
}

interface CategoryPageViewed {
  tier0: string;
  tier1?: string;
  tier2?: string;
  tier3?: string;
  totalProductsCount: number;
  query: {
    tier1?: string,
    tier2?: string,
    tier3?: string,
    year?: string,
    make?: string,
    model?: string,
  };
  availabilityCount: Record<"inStock" | "outOfStock", number>;
  brandsCount: Record<string, number>;
  pageNumber: number;
  totalPages: number;
  sortBy: string;
  selectedMake?: string;
  selectedModel?: string;
  selectedYear?: string;
  top5ProductSkus?: Array<string>;
  postalCode?: string;
  viewType: "grid" | "list";
}

interface CategoryPageNoProducts {
  category: ICategory;
  query: {
    availabilities?: string[];
    tier1?: string;
    tier2?: string;
    tier3?: string;
    year?: string;
    make?: string;
    model?: string;
  };
  selectedVehicle: IVehicle | null;
  url: string;
}

interface ISearch {
  inputText: string,
  selectedVehicle: IVehicle | string,
  searchContextSelectedVehicle: IVehicle | string,
  sourcePageType: string,
  // redirectUrl & selectedValue will be present only in Search_Suggestion_Clicked eventType
  redirectUrl?: string,
  selectedValue?: string,
}

export const AMP_CATEGORY_LOCATIONS = {
  TOOLBAR: "TOOLBAR",
  SLIDE_MENU: "SLIDE_MENU",
  DEPARTMENT_MENU: "DEPARTMENT_MENU",
  MAIN_CATEGORY_GRID: "MAIN_CATEGORY_GRID",
  REGULAR_SERVICE: "REGULAR_SERVICE",
  CATEGORY_PAGE_FILTER: "CATEGORY_PAGE_FILTER",
};

export const AMP_CHECKOUT_LOCATIONS = {
  STANDARD_CHECKOUT_TOP: "STANDARD_CHECKOUT_TOP",
  STANDARD_CHECKOUT_BOTTOM: "STANDARD_CHECKOUT_BOTTOM",
  PAYPAL_CHECKOUT_SHOPPING_CART: "PAYPAL_CHECKOUT_SHOPPING_CART",
  PAYPAL_CHECKOUT_S1_PAGE: "PAYPAL_CHECKOUT_S1_PAGE",
  PAYPAL_CHECKOUT_S2_PAGE: "PAYPAL_CHECKOUT_S2_PAGE",
  PAYPAL_CHECKOUT_S3_PAGE: "PAYPAL_CHECKOUT_S3_PAGE",
  PAYPAL_CHECKOUT_S4_PAGE: "PAYPAL_CHECKOUT_S4_PAGE",
  SEZZLE_CHECKOUT_S3_PAGE: "SEZZLE_CHECKOUT_S3_PAGE",
  SEZZLE_CHECKOUT_S4_PAGE: "SEZZLE_CHECKOUT_S4_PAGE",
  FLEXITI_CHECKOUT_S3_PAGE: "FLEXITI_CHECKOUT_S3_PAGE",
  FLEXITI_CHECKOUT_S4_PAGE: "FLEXITI_CHECKOUT_S4_PAGE",
  GOOGLE_PAY_CHECKOUT_S3_PAGE: "GOOGLE_PAY_CHECKOUT_S3_PAGE",
  GOOGLE_PAY_CHECKOUT_S4_PAGE: "GOOGLE_PAY_CHECKOUT_S4_PAGE",
  APPLE_PAY_CHECKOUT_S3_PAGE: "APPLE_PAY_CHECKOUT_S3_PAGE",
  APPLE_PAY_CHECKOUT_S4_PAGE: "APPLE_PAY_CHECKOUT_S4_PAGE",
  AFFIRM_CHECKOUT_S3_PAGE: "AFFIRM_CHECKOUT_S3_PAGE",
  AFFIRM_CHECKOUT_S4_PAGE: "AFFIRM_CHECKOUT_S4_PAGE",
};

export const AMP_ADD_TO_CART_LOCATIONS = {
  CATEGORY_PAGE: "CATEGORY_PAGE",
  PRODUCT_PAGE: "PRODUCT_PAGE",
  HORIZONTAL_PRODUCT_CARD: "HORIZONTAL_PRODUCT_CARD",
  ORDER_STATUS_BUY_IT_AGAIN: "ORDER_STATUS_BUY_IT_AGAIN",
  SHOPPING_CART_QUICK_ADD: "SHOPPING_CART_QUICK_ADD",
  AUTO_SYNC_MODAL: "AUTO_SYNC_MODAL",
};

export const AMP_SELECT_VEHICLE_LOCATIONS = {
  HOME_PAGE_BANNER: "HOME_PAGE_BANNER",
  HEADER: "HEADER",
  CATEGORY_PAGE_FILTER: "CATEGORY_PAGE_FILTER",
  SEARCH_RESULT: "SEARCH_RESULT",
  MODAL: "MODAL",
};

export const AMP_EVENTS = {
  T0_CLICKED: "T0_Clicked",
  T1_CLICKED: "T1_Clicked",
  T2_CLICKED: "T2_Clicked",
  T3_CLICKED: "T3_Clicked",
  CART_CHECKOUT: "Cart_Checkout",
  SHOPPING_CART_CLICKED: "Shopping_Cart_Clicked",
  SHOPPING_CART_VIEWED: "Shopping_Cart_Viewed",
  ADD_TO_CART_ATTEMPTED: "Add_To_Cart_Attempted",
  ADD_TO_CART_CONFIRMED: "Add_To_Cart_Confirmed",
  VEHICLE_SELECTED: "Vehicle_Selected",
  ZENDESK_SHARED_SHOPPING_CART_VIEW: "Zendesk_Shared_Shopping_Cart_Viewed",
  ORDER_PLACED: "Order_Placed",
  CART_ITEM_REMOVED: "Cart_Item_Removed",

  /**
   * Login Actions
   */
  GOOGLE_LOGIN_ATTEMPTED: "Google_Login_Attempted",
  GOOGLE_LOGIN_SUCCESS: "Google_Login_Success",
  GOOGLE_LOGIN_FAILED: "Google_Login_Failed",
  FACEBOOK_LOGIN_ATTEMPTED: "Facebook_Login_Attempted",
  FACEBOOK_LOGIN_SUCCESS: "Facebook_Login_Success",
  FACEBOOK_LOGIN_FAILED: "Facebook_Login_Failed",
  EMAIL_LOGIN_ATTEMPTED: "Email_Login_Attempted",
  EMAIL_LOGIN_SUCCESS: "Email_Login_Success",
  EMAIL_LOGIN_FAILED: "Email_Login_Failed",

  /**
   *
   * SignUp Actions
   */
  EMAIL_SIGNUP_ATTEMPTED: "Email_Signup_Attempted",
  EMAIL_SIGNUP_SUCCESS: "Email_Signup_Success",
  EMAIL_SIGNUP_FAILED: "Email_Signup_Failed",
  GOOGLE_SIGNUP_ATTEMPTED: "Google_Signup_Attempted",
  GOOGLE_SIGNUP_SUCCESS: "Google_Signup_Success",
  GOOGLE_SIGNUP_FAILED: "Google_Signup_Failed",
  FACEBOOK_SIGNUP_ATTEMPTED: "Facebook_Signup_Attempted",
  FACEBOOK_SIGNUP_SUCCESS: "Facebook_Signup_Success",
  FACEBOOK_SIGNUP_FAILED: "Facebook_Signup_Failed",

  /**
   * Home page events
   */
  HOME_PAGE_VIEWED: "Home_Page_Viewed",
  HOME_RECOMMENDATION_CARD_ITEM_CLICKED: "Home_Recommendation_Card_Item_Clicked",

  /**
   * Category events
   */
  T0_VIEWED: "T0_Viewed",
  T1_VIEWED: "T1_Viewed",
  T2_VIEWED: "T2_Viewed",
  T3_VIEWED: "T3_Viewed",
  CATEGORY_PAGE_NO_PRODUCTS: "Category_Page_No_Products",
  VEHICLE_ANNOTATIONS_SECTION_VIEWED: "Vehicle_Annotations_Section_Viewed",
  VEHICLE_ANNOTATION_CLICKED: "Vehicle_Annotation_Clicked",
  VEHICLE_ANNOTATION_HOVERED: "Vehicle_Annotation_Hovered",

  /**
   * Product page viewed
   */
  PRODUCT_PAGE_VIEWED: "Product_Page_Viewed",

  /**
   * Product Fitment
   */
  PRODUCT_VEHICLE_FIT: "Product_Vehicle_Fit",
  PRODUCT_VEHICLE_NO_FIT: "Product_Vehicle_No_Fit",

  /**
   * Hear about us event clicked
   * */
  HEAR_ABOUT_US_CLICKED: "Hear_About_Us_Clicked",
  CUSTOMER_SUPPORT_CHAT: "Customer_Support_Chat",
  SEARCH_BOX_SUBMITTED: "Search_Box_Submitted",
  SEARCH_SUGGESTION_CLICKED: "Search_Suggestion_Clicked",
  PAYPAL_STARTED: "PAYPAL_STARTED",
  PAYPAL_COMPLETED: "PAYPAL_COMPLETED",
  FLEXITI_STARTED: "FLEXITI_STARTED",
  FLEXITI_COMPLETED: "FLEXITI_COMPLETED",
  SEZZLE_STARTED: "SEZZLE_STARTED",
  SEZZLE_COMPLETED: "SEZZLE_COMPLETED",

  /**
   * Guaranteed fit product
   * */
  GUARANTEED_FIT_PRODUCT: "GUARANTEED_FIT_PRODUCT",

  /**
   * Safari Home Page Event
   */
  SAFART_HP_USER_INTERACTION: "SAFART_HP_USER_INTERACTION",

  /**
   * Checkout Events
   */

  S1_VIEWED: "S1_Viewed",
  S2_VIEWED: "S2_Viewed",
  S3_VIEWED: "S3_Viewed",
  S4_VIEWED: "S4_Viewed",
  S5_VIEWED: "S5_Viewed",
  POST_SALE_MODAL_OPENED: "POST_SALE_MODAL_OPENED",
  POST_SALE_ADD_TO_ORDER_CLICKED: "POST_SALE_ADD_TO_ORDER_CLICKED",
  POST_SALE_MODAL_CLOSED: "POST_SALE_MODAL_CLOSED",
  POST_SALE_PURCHASE_MADE: "POST_SALE_PURCHASE_MADE",

};

export const UI_TAXONOMY_VERSION = 10;

const defaultVehicleProperty = "NOT_SELECTED";

// Change this to analytics service, with setTimeOut zero.
class AmpService {
  static zendeskInteraction(zendeskAgentId: string) {
    const properties = { zId: zendeskAgentId };

    ampLogEvent(AMP_EVENTS.ZENDESK_SHARED_SHOPPING_CART_VIEW, properties);
  }

  static shoppingCartViewed() {
    ampLogEvent(AMP_EVENTS.SHOPPING_CART_VIEWED, {});
  }

  static checkoutEvent(loc: string, availableItems: IOrderedItemView[], totalCost?: number) {
    const properties = { location: loc };

    ampLogEvent(AMP_EVENTS.CART_CHECKOUT, properties);

    fbPixelTrack(FACEBOOK_PIXEL_EVENT_NAME.INITIATE_CHECKOUT, ["content_category", "content_ids",
      "contents", "currency", "num_items", "value"], undefined, availableItems, totalCost);
  }

  static orderPlaced(orderId: string, orderedItems: IOrderedItemView[], totalCost: number, isPostSale = false) {
    const { authenticationState } = getStore().getState();
    const { businessCustomer } = authenticationState;
    ampLogEvent(AMP_EVENTS.ORDER_PLACED, { orderId, businessCustomer, ...(isPostSale ? { isPostSale: true } : {}) });

    if (!isPostSale) {
      fbPixelTrack(FACEBOOK_PIXEL_EVENT_NAME.PURCHASE, ["content_ids", "content_type",
        "contents", "currency", "num_items", "value", "orderId"], undefined, orderedItems, totalCost, orderId);
    }
  }

  static removeCartItem(sku: string) {
    ampLogEvent(AMP_EVENTS.CART_ITEM_REMOVED, { sku });
  }

  static addToCartAttempted(
    product: any,
    selectedVehicle: IVehicle,
    loc: string,
  ) {
    const {
      price, quantity, manufacturer, partNumber, sku, productLines,
    } = product;
    const eventProperties = {
      price: price / 100,
      quantity,
      manufacturer,
      partNumber,
      sku,
      selectedVehicle: selectedVehicle || defaultVehicleProperty,
      productLines: "NOT_PRESENT",
      location: loc,
    };
    if (!isEmpty(productLines)) {
      eventProperties.productLines = productLines
        .filter((tier: any) => !isEmpty(tier))
        .join("/");
    }
    ampLogEvent(AMP_EVENTS.ADD_TO_CART_ATTEMPTED, eventProperties);
  }

  static addToCartConfirmed(product: any, loc: string) {
    const { sku } = product;
    const eventProperties = { sku, location: loc };
    ampLogEvent(AMP_EVENTS.ADD_TO_CART_CONFIRMED, eventProperties);
  }

  static categoryClick(category: ICategory, loc: string): void {
    const tier0 = category.tier0?.toUpperCase();
    const tier1 = category.tier1?.toUpperCase();
    const tier2 = category.tier2?.toUpperCase();
    const tier3 = category.tier3?.toUpperCase();
    let tierName = "";
    if (tier0 && tier1 && tier2 && tier3) {
      tierName = AMP_EVENTS.T3_CLICKED;
    } else if (tier0 && tier1 && tier2) {
      tierName = AMP_EVENTS.T2_CLICKED;
    } else if (tier0 && tier1) {
      tierName = AMP_EVENTS.T1_CLICKED;
    } else if (tier0) {
      tierName = AMP_EVENTS.T0_CLICKED;
    }
    if (!tierName) {
      return;
    }
    ampLogEvent(tierName, {
      tier0,
      tier1,
      tier2,
      tier3,
      location: loc,
    });
  }

  static clickShoppingCart(userCart: IShoppingCartStateReducer) {
    const { availableItems } = userCart;
    if (availableItems.length === 0) {
      return;
    }

    ampLogEvent(AMP_EVENTS.SHOPPING_CART_CLICKED, {});
  }

  static vehicleChange(vehicle: IVehicle, loc: string) {
    if (!loc) {
      return;
    }
    ampLogEvent(AMP_EVENTS.VEHICLE_SELECTED, {
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      fitmentOptions: vehicle.fitmentOptions,
      location: loc,
    });
    if (validateVehicle(vehicle)) {
      KlaviyoTrackingService.vehicleSelectedEvent(vehicle);
    }
  }

  static filterCategory(productFilter: any, filterKey: string, item: any) {
    const { tier0 } = productFilter;
    let tier1;
    let tier2;
    let tier3;

    switch (filterKey) {
    case "tier1":
      tier1 = productFilter.tier1;
      break;
    case "tier2":
      tier1 = productFilter.tier1;
      tier2 = productFilter.tier2;
      break;
    case "tier3":
      tier1 = productFilter.tier1;
      tier2 = productFilter.tier2;
      tier3 = productFilter.tier3;
      break;
    default:
    }

    if (!item) {
      AmpService.categoryClick(
        {
          tier0,
          tier1,
          tier2,
          tier3,
        },
        AMP_CATEGORY_LOCATIONS.CATEGORY_PAGE_FILTER,
      );
      return;
    }

    if (isTier2Page(item.pageType)) {
      tier2 = item.bucket;
    } else if (isTier3Page(item.pageType)) {
      tier3 = item.bucket;
    } else if (isTier1Page(item.pageType)) {
      tier1 = item.bucket;
    }

    AmpService.categoryClick(
      {
        tier0,
        tier1,
        tier2,
        tier3,
      },
      AMP_CATEGORY_LOCATIONS.CATEGORY_PAGE_FILTER,
    );
  }

  static S1Viewed() {
    ampLogEvent(AMP_EVENTS.S1_VIEWED);
  }

  static S2Viewed() {
    ampLogEvent(AMP_EVENTS.S2_VIEWED);
  }

  static S3Viewed() {
    ampLogEvent(AMP_EVENTS.S3_VIEWED);
  }

  static S4Viewed() {
    ampLogEvent(AMP_EVENTS.S4_VIEWED);
  }

  static S5Viewed(orderId: string) {
    ampLogEvent(AMP_EVENTS.S5_VIEWED, { orderId });
  }

  static postSaleModalOpened(orderId: string) {
    ampLogEvent(AMP_EVENTS.POST_SALE_MODAL_OPENED, { orderId });
  }

  static postSaleModalClosed(orderId: string, modalStep: number, method: string) {
    ampLogEvent(AMP_EVENTS.POST_SALE_MODAL_CLOSED, { orderId, modalStep, method });
  }

  static postSaleAddToOrderClicked(orderId: string) {
    ampLogEvent(AMP_EVENTS.POST_SALE_ADD_TO_ORDER_CLICKED, { orderId });
  }

  static postSalePurchaseMade(orderId: string) {
    ampLogEvent(AMP_EVENTS.POST_SALE_PURCHASE_MADE, { orderId });
  }

  static GoogleLoginAttempted() {
    ampLogEvent(AMP_EVENTS.GOOGLE_LOGIN_ATTEMPTED);
  }

  static GoogleLoginSuccess(email: string) {
    ampLogEvent(AMP_EVENTS.GOOGLE_LOGIN_SUCCESS);
    KlaviyoTrackingService.identifyKlaviyoUser(email);
  }

  static GoogleLoginFailed({ reason }: { reason: string }) {
    ampLogEvent(AMP_EVENTS.GOOGLE_LOGIN_FAILED, { reason });
  }

  static FacebookLoginAttempted() {
    ampLogEvent(AMP_EVENTS.FACEBOOK_LOGIN_ATTEMPTED);
  }

  static FacebookLoginSuccess(email: string) {
    ampLogEvent(AMP_EVENTS.FACEBOOK_LOGIN_SUCCESS);
    KlaviyoTrackingService.identifyKlaviyoUser(email);
  }

  static FacebookLoginFailed({ reason }: { reason: string }) {
    ampLogEvent(AMP_EVENTS.FACEBOOK_LOGIN_FAILED, { reason });
  }

  static EmailLoginAttempted() {
    ampLogEvent(AMP_EVENTS.EMAIL_LOGIN_ATTEMPTED);
  }

  static EmailLoginSuccess(email: string) {
    ampLogEvent(AMP_EVENTS.EMAIL_LOGIN_SUCCESS);
    KlaviyoTrackingService.identifyKlaviyoUser(email);
  }

  static EmailLoginFailed({ reason }: { reason: string }) {
    ampLogEvent(AMP_EVENTS.EMAIL_LOGIN_FAILED, { reason });
  }

  static EmailSignupAttempted() {
    ampLogEvent(AMP_EVENTS.EMAIL_SIGNUP_ATTEMPTED);
  }

  static EmailSignupSuccess(email: string) {
    ampLogEvent(AMP_EVENTS.EMAIL_SIGNUP_SUCCESS);
    KlaviyoTrackingService.identifyKlaviyoUser(email);
  }

  static EmailSignupFailed({ reason }: { reason: string }) {
    ampLogEvent(AMP_EVENTS.EMAIL_SIGNUP_FAILED, { reason });
  }

  static GoogleSignupAttempted() {
    ampLogEvent(AMP_EVENTS.GOOGLE_SIGNUP_ATTEMPTED);
  }

  static GoogleSignupSuccess() {
    ampLogEvent(AMP_EVENTS.GOOGLE_SIGNUP_SUCCESS);
  }

  static GoogleSignupFailed({ reason }: { reason: string }) {
    ampLogEvent(AMP_EVENTS.GOOGLE_LOGIN_FAILED, { reason });
  }

  static FacebookSignupAttempted() {
    ampLogEvent(AMP_EVENTS.FACEBOOK_SIGNUP_ATTEMPTED);
  }

  static FacebookSignupSuccess() {
    ampLogEvent(AMP_EVENTS.FACEBOOK_SIGNUP_SUCCESS);
  }

  static HomePageViewed() {
    ampLogEvent(AMP_EVENTS.HOME_PAGE_VIEWED);
  }

  static homeRecommendationCardItemClicked({
    cardKey,
    itemLabel,
    itemLink,
    selectedVehicle,
    loggedIn,
  }: {
    cardKey: string;
    itemLabel: string;
    itemLink: string;
    selectedVehicle: IVehicle | null;
    loggedIn: boolean;
  }) {
    ampLogEvent(AMP_EVENTS.HOME_RECOMMENDATION_CARD_ITEM_CLICKED, {
      cardKey,
      itemLabel,
      itemLink,
      selectedVehicle: selectedVehicle || defaultVehicleProperty,
      loggedIn,
    });
  }

  static ProductPageViewed({
    uiPrice,
    percentDiscount = "NOT_PRESENT",
    postalCode,
  }: {
    uiPrice: number;
    percentDiscount?: string;
    postalCode?: string;
  }, product: IEProduct) {
    const { sku } = product;
    ampLogEvent(AMP_EVENTS.PRODUCT_PAGE_VIEWED, {
      sku,
      uiPrice,
      percentDiscount,
      postalCode,
    });
    // Facebook pixel page view events will be sent after 5sec to improve performance
    if (window.fbq) {
      fbPixelTrack(FACEBOOK_PIXEL_EVENT_NAME.VIEW_CONTENT, ["content_ids", "content_category",
        "content_name", "content_type", "contents", "currency", "value"], product);
    } else {
      setTimeout(() => {
        fbPixelTrack(FACEBOOK_PIXEL_EVENT_NAME.VIEW_CONTENT, ["content_ids", "content_category",
          "content_name", "content_type", "contents", "currency", "value"], product);
      }, 5000);
    }
  }

  static ProductVehicleFitment({
    product, vehicle, fitment,
  }: {
    product: ICompactProduct;
    vehicle: IVehicle;
    fitment: boolean;
  }) {
    ampLogEvent(fitment ? AMP_EVENTS.PRODUCT_VEHICLE_FIT : AMP_EVENTS.PRODUCT_VEHICLE_NO_FIT, {
      product,
      vehicle,
    });
  }

  static categoryPageViewed(category: CategoryPageViewed): void {
    const tier0 = category.tier0?.toUpperCase();
    const tier1 = category.tier1?.toUpperCase();
    const tier2 = category.tier2?.toUpperCase();
    const tier3 = category.tier3?.toUpperCase();
    let tierName = "";
    if (tier0 && tier1 && tier2 && tier3) {
      tierName = AMP_EVENTS.T3_VIEWED;
    } else if (tier0 && tier1 && tier2) {
      tierName = AMP_EVENTS.T2_VIEWED;
    } else if (tier0 && tier1) {
      tierName = AMP_EVENTS.T1_VIEWED;
    } else if (tier0) {
      tierName = AMP_EVENTS.T0_VIEWED;
    }

    // fb pixel Category_Page_Viewed
    if (!tierName) {
      return;
    }
    ampLogEvent(tierName, category);
  }

  static categoryPageNoProducts(category: CategoryPageNoProducts): void {
    ampLogEvent(AMP_EVENTS.CATEGORY_PAGE_NO_PRODUCTS, category);
  }

  static sendHearAboutUsEvent(hearAboutUs: HearAboutUs): void {
    ampLogEvent(AMP_EVENTS.HEAR_ABOUT_US_CLICKED, hearAboutUs);
  }

  static searchEvent(userAction: string, payload: ISearch) {
    if (userAction === "search_enter" || userAction === "search_click") {
      ampLogEvent(AMP_EVENTS.SEARCH_BOX_SUBMITTED, payload);
    } else if (userAction === "search_suggestion_click") {
      ampLogEvent(AMP_EVENTS.SEARCH_SUGGESTION_CLICKED, payload);
    }
  }

  static paypalStartedEvent() {
    ampLogEvent(AMP_EVENTS.PAYPAL_STARTED);
  }

  static paypalCompletedEvent() {
    ampLogEvent(AMP_EVENTS.PAYPAL_COMPLETED);
  }

  static flexitiStartedEvent() {
    ampLogEvent(AMP_EVENTS.FLEXITI_STARTED);
  }

  static flexitiCompletedEvent() {
    ampLogEvent(AMP_EVENTS.FLEXITI_COMPLETED);
  }

  static sezzleStartedEvent() {
    ampLogEvent(AMP_EVENTS.SEZZLE_STARTED);
  }

  static sezzleCompletedEvent() {
    ampLogEvent(AMP_EVENTS.SEZZLE_COMPLETED);
  }

  static guaranteedFitProduct(payload: any, type: string) {
    const eventProperties = { ...payload, actionType: type };
    ampLogEvent(AMP_EVENTS.GUARANTEED_FIT_PRODUCT, eventProperties);
  }

  static safariHpInteraction() {
    ampLogEvent(AMP_EVENTS.SAFART_HP_USER_INTERACTION);
  }

  static vehicleAnnotationsSectionViewed(payload: any) {
    ampLogEvent(AMP_EVENTS.VEHICLE_ANNOTATIONS_SECTION_VIEWED, payload);
  }

  static vehicleAnnotationHovered(payload: any) {
    ampLogEvent(AMP_EVENTS.VEHICLE_ANNOTATION_HOVERED, payload);
  }

  static vehicleAnnotationClicked(payload: any) {
    ampLogEvent(AMP_EVENTS.VEHICLE_ANNOTATION_CLICKED, payload);
  }
}

export default tryCatchProxy(AmpService) as typeof AmpService;
